// FE-084 — un elenco di documenti che si carica a blocchi.
//
// Usato da `Storico` (cartella, recenti, preferiti, condivisi, lista), dalla
// lista «per periodo» di `Documenti.jsx` e dalla scheda Documenti del cantiere.
// La logica pura (unione senza doppioni, «ci sono altri?») e' in
// `archivioBlocchi.js`, provata da jest.
import { useCallback, useEffect, useRef, useState } from 'react';
import { documenti as documentiApi } from '../../utils/api';
import { BLOCCO_DOCUMENTI, ciSonoAltri, unisciBlocchi, caricaTutteLePagine, paginaSuccessiva } from './archivioBlocchi';

// Pagine da 200 (il massimo di `PaginazioneElenchi`) quando serve l'elenco completo.
const PAGINA_TUTTE = 200;

// `params`: i filtri dell'elenco. `limite`: un'anteprima fissa (es. «ultimi 5»),
// senza contatore ne' «Carica altri». `versione`: cambia per ricaricare da capo.
export default function useArchivioBlocchi(params, opzioni) {
  var limite = opzioni && opzioni.limite;
  var attivo = !(opzioni && opzioni.attivo === false);
  var versione = opzioni && opzioni.versione;
  var chiave = JSON.stringify(params || {});

  var [docs, setDocs] = useState([]);
  var [totale, setTotale] = useState(null);
  var [loading, setLoading] = useState(true);
  var [loadingAltri, setLoadingAltri] = useState(false);
  var [error, setError] = useState(null);
  var [errorAltri, setErrorAltri] = useState(null);
  // Scarta le risposte arrivate dopo che i filtri sono cambiati.
  var richiesta = useRef(0);

  var chiedi = useCallback(function (numero) {
    var quanti = limite || BLOCCO_DOCUMENTI;
    return documentiApi.list(Object.assign({}, JSON.parse(chiave), { page: numero, page_size: quanti }));
  }, [chiave, limite]);

  var ricarica = useCallback(async function () {
    var mia = ++richiesta.current;
    setLoading(true); setError(null); setErrorAltri(null);
    try {
      var risposta = await chiedi(1);
      if (mia !== richiesta.current) return;
      var dati = risposta.data || {};
      var righe = Array.isArray(dati) ? dati : (dati.results || []);
      setDocs(unisciBlocchi([], righe));
      setTotale(Array.isArray(dati) ? righe.length : dati.count);
    } catch (e) {
      if (mia !== richiesta.current) return;
      setError('Non è stato possibile caricare i documenti.');
    } finally {
      if (mia === richiesta.current) setLoading(false);
    }
  }, [chiedi]);

  useEffect(function () {
    if (attivo) ricarica();
  }, [ricarica, attivo, versione]);

  var caricaAltri = useCallback(async function () {
    var mia = richiesta.current;
    setLoadingAltri(true); setErrorAltri(null);
    try {
      var risposta = await chiedi(paginaSuccessiva(docs.length, limite || BLOCCO_DOCUMENTI));
      if (mia !== richiesta.current) return;
      var dati = risposta.data || {};
      setDocs(function (prima) { return unisciBlocchi(prima, dati.results || []); });
      if (typeof dati.count === 'number') setTotale(dati.count);
    } catch (e) {
      if (mia !== richiesta.current) return;
      setErrorAltri('Non è stato possibile caricare altri documenti. Riprova.');
    } finally {
      setLoadingAltri(false);
    }
  }, [chiedi, docs.length, limite]);

  // Tutto l'elenco in memoria (es. prima di un PDF). Ritorna l'elenco completo.
  var caricaTutti = useCallback(async function () {
    var mia = richiesta.current;
    setLoadingAltri(true); setErrorAltri(null);
    try {
      var tutti = await caricaTutteLePagine(function (numero) {
        return documentiApi.list(Object.assign({}, JSON.parse(chiave), { page: numero, page_size: PAGINA_TUTTE }));
      });
      if (mia === richiesta.current) {
        setDocs(tutti);
        setTotale(tutti.length);
      }
      return tutti;
    } catch (e) {
      setErrorAltri('Non è stato possibile caricare tutti i documenti. Riprova.');
      return null;
    } finally {
      setLoadingAltri(false);
    }
  }, [chiave]);

  var rimuovi = useCallback(function (id) {
    setDocs(function (prima) { return prima.filter(function (d) { return d.id !== id; }); });
    setTotale(function (t) { return typeof t === 'number' ? Math.max(0, t - 1) : t; });
  }, []);

  return {
    docs: docs,
    totale: totale,
    loading: loading,
    loadingAltri: loadingAltri,
    error: error,
    errorAltri: errorAltri,
    ciSonoAltri: !limite && ciSonoAltri(docs.length, totale),
    mostraContatore: !limite && typeof totale === 'number' && totale > 0,
    caricaAltri: caricaAltri,
    caricaTutti: caricaTutti,
    ricarica: ricarica,
    rimuovi: rimuovi,
  };
}
